import { useIsAdminRole } from '@easy-expense/data-firestore-client';
import { Layout } from '@easy-expense/ui-web-core';
import React from 'react';
import { useSearchParams } from 'react-router-dom';

import { AccountSettings } from './AccountSettings.component';
import { BillingContent } from './Billing/BillingContent.component';
import { SettingsHeader, SettingsView } from './SettingsHeader.component';
import { WorkspaceSettings } from './WorkspaceSettings/WorkspaceSettings.component';
import { ExpenseCategoriesTable } from '../Categories/CategoriesTable.component';
import { ClientsTable } from '../Clients/ClientsTable.component';
import { LocationsTable } from '../Locations/LocationsTable.component';
import { PaymentMethodsTable } from '../PaymentMethods/PaymentMethodsTable.component';
import { VendorsTable } from '../Vendors/VendorsTable.component';

function parseTabParam(value: string | null): SettingsView | undefined {
  if (!value) {
    return undefined;
  }
  if ((Object.values(SettingsView) as string[]).includes(value)) {
    return value as SettingsView;
  }
  return undefined;
}

function getInitialView(tabParam: string | null, isAdmin: boolean): SettingsView {
  const fromUrl = parseTabParam(tabParam);
  // Billing is admin-only (mirrors the tab button in SettingsHeader) — a
  // non-admin passing ?tab=billing directly falls through to the default
  // view below instead of reaching BillingContent.
  if (fromUrl && (fromUrl !== SettingsView.billing || isAdmin)) {
    return fromUrl;
  }
  if (isAdmin) {
    return SettingsView.workspace;
  }
  return SettingsView.account;
}

/**
 * The pane for the selected tab.
 *
 * Declared at module scope, which matters more than it looks: a component created inside a render
 * body is a new type on every render, so React unmounts and remounts the whole pane rather than
 * updating it. That threw away the pane's state on any re-render of SettingsContent -- including
 * the one BillingContent triggers itself when it strips the `checkout=complete` marker, which
 * killed the component mid-poll and showed a buyer the paywall they had just paid to leave
 * (EZ-5062).
 */
const SettingsDisplay: React.FC<{ settingsView: SettingsView; isAdmin: boolean }> = ({
  settingsView,
  isAdmin,
}) => {
  if (settingsView === SettingsView.workspace) {
    return <WorkspaceSettings />;
  }
  if (settingsView === SettingsView.account) {
    return <AccountSettings />;
  }
  if (settingsView === SettingsView.categories) {
    return <ExpenseCategoriesTable />;
  }
  if (settingsView === SettingsView.clients) {
    return <ClientsTable />;
  }
  if (settingsView === SettingsView.locations) {
    return <LocationsTable />;
  }
  if (settingsView === SettingsView.vendors) {
    return <VendorsTable />;
  }
  if (settingsView === SettingsView.paymentMethods) {
    return <PaymentMethodsTable />;
  }
  if (settingsView === SettingsView.billing) {
    // Defense in depth alongside the getInitialView guard above — never
    // render billing info for a non-admin, however settingsView got set.
    return isAdmin ? <BillingContent /> : null;
  }
  return null;
};

export const SettingsContent: React.FC = () => {
  const isAdmin = useIsAdminRole();
  const [searchParams, setSearchParams] = useSearchParams();
  const settingsView = getInitialView(searchParams.get('tab'), isAdmin);

  const setSettingsView = React.useCallback(
    (next: SettingsView) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.set('tab', next);
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  return (
    <Layout.Column py={24} px={32} grow style={{ height: '100vh', overflow: 'scroll' }}>
      <Layout.Column style={{ minWidth: 600, width: '100%' }} grow>
        <SettingsHeader settingsView={settingsView} setSettingsView={setSettingsView} />
        <SettingsDisplay settingsView={settingsView} isAdmin={isAdmin} />
      </Layout.Column>
    </Layout.Column>
  );
};
